import React, { useEffect, useState, useMemo } from 'react'
import { injectIntl } from 'react-intl'
import { css } from 'styled-components'
import { Map } from 'immutable'
import Button from 'components/common/Button/Button'
import SignatureModal from 'components/common/SignatureModal/SignatureModal'
import Checkbox from 'components/common/Checkbox'
import CrossInCircle from 'assets/img/cross-in-circle.png'
import { FormControl, IconButton } from '@material-ui/core'
import { connect } from 'react-redux'
import NoticeHelper from 'helpers/notice'
import SignatureIcon from 'assets/img/signature-icon.png'
import withPromise from 'helpers/withPromise'
import { Flex } from 'components/common/styles'
import DialogContent from '@material-ui/core/DialogContent'
import DialogContentText from '@material-ui/core/DialogContentText'
import DialogTitle from '@material-ui/core/DialogTitle'
import { RED } from 'constants/colors'
import SidePopover from 'components/common/Popover/SidePopover'
import { uploadFileFromDataUrl } from 'reducers/myJobs/reducer'
import { Profile } from 'types/common'
import { TABLET_BREAKPOINT } from 'helpers/responsive'
import ifWindowWidthLessThan from 'helpers/ifWindowWidthLessThan'
import ConfirmDialog from 'components/common/Popup/ConfirmDialog'
import { showNotice } from 'reducers/notice'
import Loader from 'components/Loading/SmallLoader'
import { BUTTON_TYPE_SECONDARY } from 'components/common/Button/constants'
import messages from '../../translate'
import {
  IconButtonContainer,
  SignatureContainer,
  ImageContainer,
  ActionsContainer,
  SignatureInitialsWrapper,
  InitialsContainer,
} from './JobSignatureInput.styles'
import { JobSignatureInputProps } from './JobSignatureInput.types'

const JobSignatureInput = ({
  inputInfo: { id, parameters, title, required },
  userData,
  intl: { formatMessage },
  className,
  inputData: { evtInputId, value },
  onJobTaskAutoSave,
  uploadFileFromDataUrl,
  showNotice,
  isProfileFetching,
  isTaskCompleted,
  disabled,
}: JobSignatureInputProps) => {
  const [isOpenDeleteSignature, setIsOpenDeleteSignature] = useState(false)
  const data = userData.toJS()
  const name = value?.name ? value?.name : data.name
  const { signatureUrl, signatureDataUrl } = data
  const [isOpenSignature, setIsOpenSignature] = useState(false)
  const [isSelected, setIsSelected] = useState(false)
  const [initials, setInitials] = useState<string>('')
  const [evtTaskInputId, setEvtTaskInputId] = useState<number | string>('')
  const [updatedSignature, setUpdatedSignature] = useState<string | null>(null)
  const drawAsterisk = required ? '*' : ''

  useEffect(() => {
    if (value?.file && !isSelected && !updatedSignature) {
      setIsSelected(!!value.file)
      setUpdatedSignature(value.file?.uri)
      setInitials(value.name || '')
      setEvtTaskInputId(evtInputId)
    }
  }, [JSON.stringify(value)])

  const toggleIsOpenSignature = () => setIsOpenSignature(!isOpenSignature)

  const renderSuccessMessage = () => {
    if (!parameters?.use_worker_signature) {
      showNotice({
        notice: NoticeHelper.createSuccessObj(
          formatMessage(messages.saveSuccessMessage),
        ),
      })
    }
  }

  const setResultId = ({ id }: { id: number }): void => {
    setEvtTaskInputId(id)
  }

  const updateEvtTask = (
    isCheckboxSelected: boolean,
    newSignature?: string | null,
    name?: string,
  ) => {
    if (name) {
      setInitials(name)
    }

    if (isCheckboxSelected) {
      withPromise(uploadFileFromDataUrl, {
        file: newSignature || signatureDataUrl,
      }).then(response => {
        onJobTaskAutoSave(
          {
            evtInputId,
            result: {
              file: response.id,
              name: name || data.name || null,
            },
            input: id,
            setResultId,
          },
          renderSuccessMessage,
        )
      })
    } else {
      onJobTaskAutoSave(
        {
          evtInputId: evtInputId || evtTaskInputId,
          result: null,
          input: id,
          setResultId,
        },
        renderSuccessMessage,
      )
    }
  }

  const onCheckboxClick = (event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation()

    if (!disabled && parameters?.use_worker_signature) {
      setIsSelected(!isSelected)
      updateEvtTask(!isSelected)
    }
  }

  const selectedSignature = useMemo(
    () =>
      parameters?.use_worker_signature
        ? updatedSignature || signatureUrl
        : updatedSignature,
    [updatedSignature, signatureUrl, parameters?.use_worker_signature],
  )

  const onIconButtonClick = parameters?.use_worker_signature
    ? toggleIsOpenSignature
    : () => setIsOpenDeleteSignature(true)

  const IconButtonImage = useMemo(
    () => (parameters?.use_worker_signature ? SignatureIcon : CrossInCircle),
    [parameters?.use_worker_signature],
  )

  const deleteSignature = () => {
    setUpdatedSignature(null)
    setIsSelected(false)
    updateEvtTask(false)
    setIsOpenDeleteSignature(false)
  }

  const renderSignatureModal = () => (
    <SignatureModal
      setUpdatedSignature={(signature: string | null, name?: string) => {
        setUpdatedSignature(signature)
        setIsSelected(true)
        updateEvtTask(true, signature, name)
      }}
      signatureTemplate={selectedSignature}
      isOpen={isOpenSignature}
      toggleIsOpen={toggleIsOpenSignature}
      confirmButtonText={formatMessage(messages.sign)}
      title={`${formatMessage(messages.my)} ${formatMessage(
        messages.signature,
      )}`}
      showNameField={!parameters?.use_worker_signature}
    />
  )

  return (
    <>
      <FormControl fullWidth className={className}>
        <span className="job-field-label">
          {title} {drawAsterisk}
        </span>
        <div className="job-field-wrapper">
          {isProfileFetching ||
          ((!parameters?.use_worker_signature || !signatureUrl) &&
            !updatedSignature) ? (
            <Button
              disabled={disabled}
              onClick={toggleIsOpenSignature}
              customStyles={css`
                height: 46px;
                width: 100%;
              `}
              type={BUTTON_TYPE_SECONDARY}>
              {isProfileFetching ? <Loader /> : formatMessage(messages.sign)}
            </Button>
          ) : (
            <Flex>
              <SignatureInitialsWrapper>
                <SignatureContainer
                  onClick={onCheckboxClick}
                  isActive={isSelected}>
                  <Flex align="flex-start">
                    {(updatedSignature || signatureUrl) &&
                      parameters?.use_worker_signature && (
                        <Checkbox
                          checked={isSelected}
                          disabled={isTaskCompleted}
                        />
                      )}
                    <ImageContainer align="center" justify="center">
                      <img src={selectedSignature} alt="jobs signature" />
                    </ImageContainer>
                  </Flex>
                </SignatureContainer>
                <InitialsContainer>
                  {parameters?.use_worker_signature ? name : initials}
                </InitialsContainer>
              </SignatureInitialsWrapper>
              <IconButtonContainer>
                <IconButton
                  disabled={disabled}
                  aria-label="delete jobs signature"
                  onClick={onIconButtonClick}>
                  <img src={IconButtonImage} alt="" />
                </IconButton>
              </IconButtonContainer>
            </Flex>
          )}
        </div>
        {ifWindowWidthLessThan(TABLET_BREAKPOINT) ? (
          // @ts-expect-error SidePopover is JS file
          <SidePopover
            open={isOpenSignature}
            close={toggleIsOpenSignature}
            className="modal-content"
            position="bottom">
            {renderSignatureModal()}
          </SidePopover>
        ) : (
          renderSignatureModal()
        )}
      </FormControl>
      {ifWindowWidthLessThan(TABLET_BREAKPOINT) ? (
        // @ts-expect-error SidePopover is JS file
        <SidePopover
          open={isOpenDeleteSignature}
          close={() => setIsOpenDeleteSignature(false)}
          className="modal-content"
          position="bottom">
          <DialogTitle id="confirm-dialog-title">{`${formatMessage(
            messages.delete,
          )} ${formatMessage(messages.signature).toLowerCase()}`}</DialogTitle>
          <DialogContent>
            <DialogContentText id="confirm-dialog-message">
              {formatMessage(messages.deleteSignatureInputMessage)}
            </DialogContentText>
          </DialogContent>
          <ActionsContainer direction="column">
            <Button
              onClick={() => setIsOpenDeleteSignature(false)}
              type={BUTTON_TYPE_SECONDARY}
              customStyles={css`
                height: 50px;
                width: 100%;
                margin-bottom: 16px;
              `}>
              {formatMessage(messages.cancel)}
            </Button>
            <Button
              onClick={deleteSignature}
              customStyles={css`
                height: 50px;
                width: 100%;
                background: ${RED};
                margin-bottom: 24px;
              `}>
              {formatMessage(messages.delete)}
            </Button>
          </ActionsContainer>
        </SidePopover>
      ) : (
        <ConfirmDialog
          // @ts-expect-error ConfirmDialog is JS file
          open={isOpenDeleteSignature}
          cancel={{
            color: 'default',
            variant: 'outlined',
            customText: formatMessage(messages.cancel),
          }}
          confirm={{
            color: 'scales-secondary',
            customText: formatMessage(messages.delete),
          }}
          onClose={() => setIsOpenDeleteSignature(false)}
          onConfirmAction={deleteSignature}
          className="dialog-primary-custom"
          title={`${formatMessage(messages.delete)} ${formatMessage(
            messages.signature,
          ).toLowerCase()}`}
          message={formatMessage(messages.deleteSignatureInputMessage)}
        />
      )}
    </>
  )
}

export default connect(
  (state: {
    getIn: (path: Array<string>) => Map<string, Profile> | boolean
  }) => ({
    userData: state.getIn(['profile', 'profile']) as Map<string, Profile>,
    isProfileFetching: state.getIn(['profile', 'isProfileFetching']) as boolean,
  }),
  {
    uploadFileFromDataUrl,
    showNotice,
  },
)(injectIntl(JobSignatureInput))
